import ImageGalleryModal from '@components/Shared/ImageGalleryModal';
import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import type { Inspection } from '@lib/spproject/types';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { PanelBody, SectionLabel, SnapshotGrid, StatCard, StatGrid } from './NavOverlay.styled';

interface InspectionDetailPanelProps {
  inspection: SPInspection;
}

const STATUS_ORDER: Inspection.NoticeType[] = ['Ok', 'Warning', 'Error', 'NotChecked'];

const STATUS_LABEL_KEY: Record<Inspection.NoticeType, string> = {
  Ok: 'Common_Ok',
  Warning: 'Common_Warning',
  Error: 'Common_Error',
  NotChecked: 'Common_Pending',
};

const InspectionDetailPanel: FC<InspectionDetailPanelProps> = ({ inspection }) => {
  const { currentLanguage, t } = useLocalization();
  const noticeColors = useNoticeColors();

  const [snapshots, setSnapshots] = useState<Inspection.NoteResource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
  const [startIndex, setStartIndex] = useState<number>(0);

  const info = inspection.inspection.info;
  const pois = useMemo(() => inspection.inspection.pointOfInterests ?? [], [inspection]);

  const statusCounts = useMemo(() => {
    const counts: Record<Inspection.NoticeType, number> = { NotChecked: 0, Ok: 0, Warning: 0, Error: 0 };
    for (const poi of pois) counts[SPInspection.getPOIStatus(poi)]++;
    return counts;
  }, [pois]);

  useEffect(() => {
    let urls: string[] = [];
    let active = true;

    setLoading(true);

    inspection.getResources(SPInspection.isSnapshot).then((result) => {
      if (!active) {
        result.forEach((snapshot) => snapshot.resourceUrl && URL.revokeObjectURL(snapshot.resourceUrl));
        return;
      }
      urls = result.map((snapshot) => snapshot.resourceUrl).filter(Boolean);
      setSnapshots(result);
      setLoading(false);
    });

    return () => {
      active = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [inspection]);

  const formatDate = (date: string | Date) => {
    const value = typeof date === 'string' ? new Date(date) : date;
    return value.toLocaleDateString(currentLanguage, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const images = snapshots.map((snapshot) => ({
    original: snapshot.resourceUrl,
    thumbnail: snapshot.resourceUrl,
    description: snapshot.name,
  }));

  const openGallery = (index: number) => {
    setStartIndex(index);
    setGalleryOpen(true);
  };

  return (
    <PanelBody>
      <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <SectionLabel>{t('Common_Status')}</SectionLabel>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {STATUS_ORDER.filter((status) => status !== 'NotChecked' || statusCounts[status] > 0).map((status) => {
              const color = noticeColors[status];
              return (
                <Stack
                  key={status}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{
                    px: 1.1,
                    py: 0.5,
                    borderRadius: 99,
                    backgroundColor: alpha(color, 0.14),
                    border: `1px solid ${alpha(color, 0.25)}`,
                  }}
                >
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color, lineHeight: 1 }}>
                    {statusCounts[status]}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: alpha(color, 0.85), lineHeight: 1 }}>
                    {t(STATUS_LABEL_KEY[status])}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Box>
          <SectionLabel>{t('Common_Details')}</SectionLabel>
          <StatGrid>
            <Stat icon={<CalendarTodayRoundedIcon sx={{ fontSize: 15 }} />} label={t('Common_Created')} value={formatDate(info.dateCreated)} />
            <Stat icon={<UpdateRoundedIcon sx={{ fontSize: 16 }} />} label={t('Common_Modified')} value={formatDate(info.dateModified)} />
            <Stat icon={<PlaceRoundedIcon sx={{ fontSize: 16 }} />} label={t('Common_Checkpoints')} value={String(pois.length)} />
            <Stat icon={<CollectionsRoundedIcon sx={{ fontSize: 16 }} />} label={t('Common_Snapshots')} value={loading ? '—' : String(snapshots.length)} />
          </StatGrid>
        </Box>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, px: 0.25 }}>
            <SectionLabel sx={{ m: 0 }}>{t('Common_Snapshots')}</SectionLabel>
            {!loading && snapshots.length > 0 && (
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
                {snapshots.length}
              </Typography>
            )}
          </Stack>

          {loading ? (
            <SnapshotGrid>
              {[0, 1, 2].map((index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  sx={{ pt: '100%', borderRadius: 2, bgcolor: alpha('#ffffff', 0.05) }}
                />
              ))}
            </SnapshotGrid>
          ) : snapshots.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.7, px: 0.25 }}>
              {t('InspectionDetail_NoSnapshots')}
            </Typography>
          ) : (
            <SnapshotGrid>
              {snapshots.map((snapshot, index) => (
                <Box
                  key={snapshot.id}
                  role="button"
                  tabIndex={0}
                  aria-label={t('InspectionDetail_OpenSnapshot', [snapshot.name])}
                  title={snapshot.name}
                  onClick={() => openGallery(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openGallery(index);
                    }
                  }}
                  sx={{
                    position: 'relative',
                    pt: '100%',
                    borderRadius: 2,
                    cursor: 'pointer',
                    backgroundColor: alpha('#ffffff', 0.04),
                    backgroundImage: snapshot.resourceUrl ? `url(${snapshot.resourceUrl})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: `1px solid ${alpha('#ffffff', 0.08)}`,
                    transition: 'transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.04)',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.6),
                      boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                    },
                    '&:focus-visible': {
                      outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}
                />
              ))}
            </SnapshotGrid>
          )}
        </Box>
      </Box>

      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={images}
        startIndex={startIndex}
      />
    </PanelBody>
  );
};

const Stat: FC<{ icon: ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <StatCard>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'text.secondary' }}>
      <Box sx={{ display: 'flex', opacity: 0.7 }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', opacity: 0.85 }}>
        {label}
      </Typography>
    </Stack>
    <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
      {value}
    </Typography>
  </StatCard>
);

export default InspectionDetailPanel;
